import "htmx.org";
import { createAuthClient } from "better-auth/client";
import { twoFactorClient } from "better-auth/client/plugins";
import "./styles.css";
import "./patient-search.js";
import "./scheduling.js";

const authClient = createAuthClient({
  baseURL: window.location.origin,
  plugins: [twoFactorClient({ twoFactorPage: "/two-factor" })]
});

function setError(form: HTMLFormElement, message?: string) {
  const output = form.querySelector<HTMLElement>("[data-auth-error]");
  if (!output) return;
  output.textContent = message ?? "";
  output.hidden = !message;
}

function setSubmitting(form: HTMLFormElement, submitting: boolean) {
  const button = form.querySelector<HTMLButtonElement>('button[type="submit"]');
  if (!button) return;
  button.disabled = submitting;
  button.setAttribute("aria-busy", String(submitting));
}

document.querySelector<HTMLFormElement>("[data-sign-in-form]")?.addEventListener("submit", async (event) => {
  event.preventDefault();
  const form = event.currentTarget as HTMLFormElement;
  const values = new FormData(form);
  setSubmitting(form, true);
  setError(form);

  const result = await authClient.signIn.email({
    email: String(values.get("email") ?? "").trim(),
    password: String(values.get("password") ?? "")
  });

  setSubmitting(form, false);
  if (result.error) {
    setError(form, result.error.message ?? "Sign-in failed.");
    return;
  }

  const data = result.data as { twoFactorRedirect?: boolean } | null;
  window.location.assign(data?.twoFactorRedirect ? "/two-factor" : "/");
});

const twoFactorForm = document.querySelector<HTMLFormElement>("[data-two-factor-form]");

async function sendEmailCode() {
  if (!twoFactorForm) return;
  const status = document.querySelector<HTMLElement>("[data-code-status]");
  setError(twoFactorForm);
  if (status) status.textContent = "Sending a six-digit code to your invited email address…";

  const result = await authClient.twoFactor.sendOtp({ trustDevice: false });
  if (result.error) {
    setError(twoFactorForm, result.error.message ?? "The email code could not be sent.");
    if (status) status.textContent = "We could not send a code.";
    return;
  }
  if (status) status.textContent = "A six-digit code was sent to your invited email address.";
}

if (twoFactorForm) void sendEmailCode();
document.querySelector<HTMLElement>("[data-resend-code]")?.addEventListener("click", () => void sendEmailCode());

twoFactorForm?.addEventListener("submit", async (event) => {
  event.preventDefault();
  const form = event.currentTarget as HTMLFormElement;
  const values = new FormData(form);
  setSubmitting(form, true);
  setError(form);

  const result = await authClient.twoFactor.verifyOtp({
    code: String(values.get("code") ?? ""),
    trustDevice: false
  });

  setSubmitting(form, false);
  if (result.error) {
    setError(form, result.error.message ?? "The authentication code was not accepted.");
    return;
  }
  window.location.assign("/");
});

document.querySelector<HTMLFormElement>("[data-enroll-form]")?.addEventListener("submit", async (event) => {
  event.preventDefault();
  const form = event.currentTarget as HTMLFormElement;
  const values = new FormData(form);
  setSubmitting(form, true);
  setError(form);

  const result = await authClient.twoFactor.enable({
    password: String(values.get("password") ?? ""),
    method: "otp"
  });

  setSubmitting(form, false);
  if (result.error) {
    setError(form, result.error.message ?? "Email verification could not be enabled.");
    return;
  }
  await authClient.signOut();
  window.location.assign("/sign-in");
});

document.querySelector<HTMLElement>("[data-sign-out]")?.addEventListener("click", async () => {
  await authClient.signOut();
  window.location.assign("/sign-in");
});

document.addEventListener("click", (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const trigger = target.closest<HTMLElement>("[data-modal-open]");
  if (trigger) {
    const dialogId = trigger.dataset.modalOpen;
    const dialog = dialogId ? document.getElementById(dialogId) : null;
    if (!(dialog instanceof HTMLDialogElement)) return;
    if (!dialog.open) dialog.showModal();
    window.requestAnimationFrame(() => {
      const firstField = dialog.querySelector<HTMLElement>(
        'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])'
      );
      (firstField ?? dialog.querySelector<HTMLElement>('button:not([disabled])'))?.focus();
    });
    return;
  }

  const closeButton = target.closest<HTMLElement>("[data-modal-close]");
  if (closeButton) closeButton.closest<HTMLDialogElement>("dialog")?.close();
});

document.addEventListener("click", (event) => {
  const target = event.target;
  if (target instanceof HTMLDialogElement && target.matches("[data-app-modal]")) target.close();
});

const clinicalEndpoints: Record<string, string> = {
  patient: "/api/patients",
  insurer: "/api/clinical-hours/insurers",
  "service-code": "/api/clinical-hours/service-codes",
  treatment: "/api/clinical-hours/treatments",
  authorization: "/api/clinical-hours/authorizations",
  adjustment: "/api/clinical-hours/adjustments",
  visit: "/api/clinical-hours/visits",
  "visit-finalize": "/api/clinical-hours/visit-finalizations",
  "visit-correction": "/api/clinical-hours/visit-corrections"
};

function formToObject(form: HTMLFormElement, submitter?: HTMLElement | null) {
  const values = new FormData(form, submitter instanceof HTMLButtonElement ? submitter : undefined);
  const object: Record<string, string | string[]> = {};
  for (const [key, value] of values) {
    const text = String(value);
    const current = object[key];
    if (current === undefined) object[key] = text;
    else object[key] = Array.isArray(current) ? [...current, text] : [current, text];
  }
  return object;
}

async function submitClinicalForm(form: HTMLFormElement, submitter?: HTMLElement | null) {
  const kind = form.dataset.formKind ?? "";
  const endpoint = form.dataset.formEndpoint ?? clinicalEndpoints[kind];
  if (!endpoint) return;
  const error = form.querySelector<HTMLElement>("[data-form-error]");
  const warning = form.querySelector<HTMLElement>("[data-visit-warning]");
  if (error) { error.hidden = true; error.textContent = ""; }
  setSubmitting(form, true);

  const response = await fetch(endpoint, {
    method: form.dataset.formMethod ?? "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(formToObject(form, submitter))
  });
  const result = await response.json().catch(() => null) as { message?: string; warnings?: string[]; confirmationRequired?: boolean; duplicateWarning?: boolean } | null;
  setSubmitting(form, false);

  if (!response.ok) {
    if (result?.confirmationRequired && warning) {
      warning.textContent = result.warnings?.join(" ") ?? "Confirmation is required.";
      warning.hidden = false;
      form.querySelector<HTMLElement>("[data-overage-confirmation]")?.removeAttribute("hidden");
      return;
    }
    if (error) {
      error.textContent = result?.message ?? "The record could not be saved.";
      error.hidden = false;
      error.focus();
    }
    return;
  }
  if (result?.duplicateWarning) window.alert("Potential duplicate: another visit exists for this patient, therapist, date, and service code. The visit was saved and flagged in audit history.");
  window.location.reload();
}

document.addEventListener("submit", (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches("[data-clinical-form]")) return;
  event.preventDefault();
  const submitter = event.submitter instanceof HTMLElement ? event.submitter : null;
  const confirmation = submitter?.dataset.confirm;
  if (confirmation && !window.confirm(confirmation)) return;
  void submitClinicalForm(form, submitter);
});

function syncVisitEntry(form: HTMLFormElement) {
  const patient = form.querySelector<HTMLSelectElement>("[data-visit-patient]");
  const authorization = form.querySelector<HTMLSelectElement>("[data-visit-authorization]");
  const service = form.querySelector<HTMLSelectElement>("[data-visit-service]");
  const units = form.querySelector<HTMLInputElement>("[data-visit-units]");
  if (!patient || !authorization || !service || !units) return;

  for (const option of authorization.options) {
    if (!option.value) continue;
    const matches = !patient.value || option.dataset.patientId === patient.value;
    option.hidden = !matches;
    option.disabled = !matches;
  }
  if (authorization.selectedOptions[0]?.disabled) authorization.value = "";

  for (const option of service.options) {
    if (!option.value) continue;
    const matches = !authorization.value || option.dataset.authorizationId === authorization.value;
    option.hidden = !matches;
    option.disabled = !matches;
  }
  if (service.selectedOptions[0]?.disabled) service.value = "";

  const selected = service.selectedOptions[0];
  const approved = selected?.dataset.authorized;
  const used = selected?.dataset.used;
  const remaining = selected?.dataset.remaining;
  const remainingNumber = Number(remaining);
  const visitUnits = Number(units.value || 0);
  const projected = Number.isFinite(remainingNumber) && remaining !== undefined ? remainingNumber - visitUnits : null;
  setText(form, "[data-balance-authorized]", approved ?? "—");
  setText(form, "[data-balance-used]", used ?? "—");
  setText(form, "[data-balance-remaining]", remaining ?? "—");
  setText(form, "[data-balance-projected]", projected === null ? "—" : String(projected));

  const projectedOutput = form.querySelector<HTMLElement>("[data-balance-projected]");
  projectedOutput?.classList.toggle("negative", projected !== null && projected < 0);
  const warning = form.querySelector<HTMLElement>("[data-visit-warning]");
  if (warning && projected !== null && projected < 0) {
    warning.textContent = `This visit projects an overage of ${Math.abs(projected)} unit${Math.abs(projected) === 1 ? "" : "s"}. An administrator must confirm it.`;
    warning.hidden = false;
  } else if (warning) {
    warning.hidden = true;
  }
}

function setText(root: HTMLElement, selector: string, value: string) {
  const element = root.querySelector<HTMLElement>(selector);
  if (element) element.textContent = value;
}

document.addEventListener("change", (event) => {
  const target = event.target;
  if (!(target instanceof Element) || !target.matches("[data-visit-patient], [data-visit-authorization], [data-visit-service], [data-visit-units]")) return;
  const form = target.closest<HTMLFormElement>("[data-form-kind='visit']");
  if (form) syncVisitEntry(form);
});
document.addEventListener("input", (event) => {
  const target = event.target;
  if (!(target instanceof Element) || !target.matches("[data-visit-units]")) return;
  const form = target.closest<HTMLFormElement>("[data-form-kind='visit']");
  if (form) syncVisitEntry(form);
});

// Close and reset correction popovers without submitting changes.
document.addEventListener("click", event => {
  const button = event.target instanceof Element ? event.target.closest<HTMLElement>("[data-cancel-row-action]") : null;
  if (!button) return;
  const details = button.closest<HTMLDetailsElement>("details");
  button.closest<HTMLFormElement>("form")?.reset();
  button.closest("form")?.querySelector<HTMLElement>("[data-form-error]")?.setAttribute("hidden", "");
  if (details) { details.open = false; details.querySelector<HTMLElement>("summary")?.focus(); }
});

const patientTooltip = document.createElement("div");
patientTooltip.id = "patient-name-tooltip";
patientTooltip.className = "patient-name-tooltip";
patientTooltip.setAttribute("role", "tooltip");
patientTooltip.setAttribute("popover", "manual");
const tooltipLabel = document.createElement("small"); tooltipLabel.textContent = "Patient";
const tooltipName = document.createElement("strong");
patientTooltip.append(tooltipLabel, tooltipName);
document.body.append(patientTooltip);
let tooltipTrigger: HTMLElement | null = null;
function hidePatientTooltip() {
  patientTooltip.hidePopover();
  tooltipTrigger?.removeAttribute("aria-describedby"); tooltipTrigger = null;
}
function showPatientTooltip(target: HTMLElement) {
  tooltipTrigger?.removeAttribute("aria-describedby");
  tooltipTrigger = target; tooltipName.textContent = target.dataset.patientTooltip ?? "";
  target.setAttribute("aria-describedby", patientTooltip.id);
  patientTooltip.showPopover();
  const rect = target.getBoundingClientRect();
  patientTooltip.style.left = `${Math.max(8, Math.min(rect.left, window.innerWidth - patientTooltip.offsetWidth - 8))}px`;
  patientTooltip.style.top = `${rect.bottom + patientTooltip.offsetHeight + 12 < window.innerHeight ? rect.bottom + 8 : Math.max(8, rect.top - patientTooltip.offsetHeight - 8)}px`;
}
for (const eventName of ["pointerover", "focusin", "click"]) document.addEventListener(eventName, event => {
  const target = event.target instanceof Element ? event.target.closest<HTMLElement>("[data-patient-tooltip]") : null;
  if (target) showPatientTooltip(target);
});
document.addEventListener("pointerout", event => { if (event.target instanceof Element && event.target.closest("[data-patient-tooltip]")) hidePatientTooltip(); });
document.addEventListener("focusout", event => { if (event.target instanceof Element && event.target.closest("[data-patient-tooltip]")) hidePatientTooltip(); });
document.addEventListener("keydown", event => { if (event.key === "Escape") hidePatientTooltip(); });
document.addEventListener("click", event => { if (event.target instanceof Element && !event.target.closest("[data-patient-tooltip],#patient-name-tooltip")) hidePatientTooltip(); });
window.addEventListener("scroll", hidePatientTooltip, true);
